import React, { useEffect, useRef, useState } from 'react'
import KeyboardDoubleArrowRightIcon from '@mui/icons-material/KeyboardDoubleArrowRight'
import KeyboardDoubleArrowLeftIcon from '@mui/icons-material/KeyboardDoubleArrowLeft'
import { Box } from '@mui/material'
import styled from '@emotion/styled'

interface PointerProps {
  isVisible: boolean
}

const LeftPointer = styled(KeyboardDoubleArrowLeftIcon, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})<PointerProps>(({ isVisible }) => ({
  position: 'absolute',
  left: -20,
  top: -8,
  transform: isVisible ? 'translateX(-25px)' : '',
  zIndex: 10,
  transition: 'opacity 0.3s, transform 0.5s',
  opacity: isVisible ? 1 : 0,
}))

const RightPointer = styled(KeyboardDoubleArrowRightIcon, {
  shouldForwardProp: (prop) => prop !== 'isVisible',
})<PointerProps>(({ isVisible }) => ({
  position: 'absolute',
  right: -20,
  bottom: 12,
  transform: isVisible ? 'translateX(25px)' : '',
  zIndex: 10,
  transition: 'opacity 0.3s, transform 0.5s',
  opacity: isVisible ? 1 : 0,
}))

function findScrollableParent(element: HTMLElement | null): HTMLElement | null {
  if (!element) {
    return null
  }

  const overflowY = window.getComputedStyle(element).overflowY

  if (
    element.tagName === 'DIV' &&
    (overflowY === 'auto' || overflowY === 'scroll' || overflowY === 'overlay')
  ) {
    return element
  }

  return findScrollableParent(element.parentElement)
}

const ScrollDebounce = 200
const NextTabDelay = ScrollDebounce * 1.4

interface ScrollBetweenTabsWrapperProps {
  children: React.ReactNode
  tabIndex: number
  tabValue: number
  setTabValue: (v: number) => void
  totalTabs: number
}

export const ScrollBetweenTabsWrapper: React.FC<
  ScrollBetweenTabsWrapperProps
> = ({ children, tabIndex, tabValue, setTabValue, totalTabs }) => {
  const ref = useRef<HTMLDivElement>(null)

  const scrollingTimeout = useRef<NodeJS.Timeout | null>(null)
  const scrollableParent = useRef<HTMLDivElement | null>(null)
  const scrollingStart = useRef<Date | null>(null)
  const scrollingDirection = useRef<'up' | 'down' | null>(null)

  const [isScrolling, setIsScrolling] = useState<boolean | null>(null)

  const handleWheelTransition = (
    e: WheelEvent,
    atScrollingEdge: () => boolean,
    isTabIndexAtEdge: boolean,
    isScrollingTowardsEdge: boolean,
    isScrollingInOppositeDirection: (ev: WheelEvent) => boolean,
  ) => {
    if (
      tabIndex !== tabValue ||
      isScrolling ||
      !isScrollingTowardsEdge ||
      isTabIndexAtEdge
    )
      return

    const el = ref.current
    if (!scrollableParent.current || !el) return

    if (atScrollingEdge()) {
      const direction = e.deltaY > 0 ? 'down' : 'up'
      setIsScrolling(true)
      scrollingDirection.current = direction

      if (scrollingTimeout.current) clearTimeout(scrollingTimeout.current)

      // Animation: move div slightly
      el.style.transform = `translateY(${direction === 'down' ? -20 : 20}px)`

      scrollingTimeout.current = setTimeout(() => {
        el.style.transform = ''
        setIsScrolling(false)
      }, ScrollDebounce)

      // If user scrolls in the opposite direction, cancel
      const cancel = (ev: WheelEvent) => {
        if (isScrollingInOppositeDirection(ev)) {
          if (scrollingTimeout.current) clearTimeout(scrollingTimeout.current)
          el.style.transform = ''
          setIsScrolling(null)
          scrollableParent.current?.removeEventListener('wheel', cancel, true)
        }
      }
      scrollableParent.current.addEventListener('wheel', cancel, true)
    }
  }

  const handleWheel = (e: WheelEvent) => {
    // Up
    handleWheelTransition(
      e,
      () => scrollableParent.current!.scrollTop < 2,
      tabIndex === 0,
      e.deltaY < 0,
      (ev) => ev.deltaY > 0,
    )

    // Down
    handleWheelTransition(
      e,
      () =>
        scrollableParent.current!.scrollHeight -
          scrollableParent.current!.scrollTop -
          scrollableParent.current!.clientHeight <
        2,
      tabIndex === totalTabs - 1,
      e.deltaY > 0,
      (ev) => ev.deltaY < 0,
    )
  }

  useEffect(() => {
    if (ref.current) {
      const parent = findScrollableParent(ref.current) || ref.current
      scrollableParent.current = parent as HTMLDivElement
      scrollableParent.current.addEventListener('wheel', handleWheel)
    }

    return () => {
      if (scrollingTimeout.current) clearTimeout(scrollingTimeout.current)
      if (scrollableParent.current)
        scrollableParent.current.removeEventListener('wheel', handleWheel)
    }
  }, [])

  useEffect(() => {
    if (isScrolling === null) {
      // If user scrolls in the opposite direction, cancel
      scrollingStart.current = null
    } else if (isScrolling) {
      // If user scrolls down and is at the bottom
      // or user scrolls up and is at the top
      // set start time
      scrollingStart.current = new Date()
    } else if (scrollingTimeout.current) {
      // If user stops scrolling, check if enough time has passed
      if (
        new Date().getTime() - scrollingStart.current!.getTime() >
        NextTabDelay
      ) {
        setTabValue(tabValue + (scrollingDirection.current === 'down' ? 1 : -1))

        // if scrolling up, set the scroll position to the bottom
        // so that the user can see the end of the previous tab content
        if (scrollingDirection.current === 'up') {
          setTimeout(() => {
            if (scrollableParent.current)
              scrollableParent.current.scrollTop =
                scrollableParent.current.scrollHeight
          }, 100)
        }
      }
    }
  }, [isScrolling, scrollingTimeout.current])

  return (
    <Box position={'relative'}>
      <LeftPointer
        isVisible={isScrolling === true && scrollingDirection.current === 'up'}
        fontSize="large"
        color="primary"
      />
      <Box sx={{ transition: 'transform 0.3s' }} ref={ref} tabIndex={0}>
        {children}
      </Box>
      <RightPointer
        isVisible={
          isScrolling === true && scrollingDirection.current === 'down'
        }
        fontSize="large"
        color="primary"
      />
    </Box>
  )
}
